/**
 * Authentication Utilities
 *
 * Security-focused utilities for authentication flows.
 *
 * [Source: docs/stories/4-1-implement-better-auth-authentication.md#R-4.1-004]
 */

/**
 * Validates a callback URL to prevent open redirect attacks.
 *
 * Only allows:
 * - Relative paths (starting with /)
 * - Same-origin URLs (matching BETTER_AUTH_URL or current origin)
 *
 * @param callbackUrl - The URL to validate
 * @param baseUrl - The base URL of the application (optional, defaults to BETTER_AUTH_URL)
 * @returns The validated URL, or the default fallback if invalid
 *
 * @example
 * validateCallbackUrl('/sites'); // Returns '/sites'
 * validateCallbackUrl('https://evil.com'); // Returns '/sites' (rejected)
 * validateCallbackUrl('/sites/my-site/settings'); // Returns '/sites/my-site/settings'
 *
 * [Source: docs/stories/4-1-implement-better-auth-authentication.md#P0-SEC-R-4.1-004]
 */
export function validateCallbackUrl(
  callbackUrl: string | null,
  baseUrl?: string,
  defaultPath = '/sites'
): string {
  // No callback URL provided - use default
  if (!callbackUrl) {
    return defaultPath;
  }

  // Trim whitespace
  const trimmed = callbackUrl.trim();

  // Empty after trim - use default
  if (!trimmed) {
    return defaultPath;
  }

  // Relative URLs starting with / are safe (but not // which is protocol-relative)
  if (trimmed.startsWith('/') && !trimmed.startsWith('//')) {
    // Additional check: no backslashes (URL encoding attack)
    if (trimmed.includes('\\')) {
      return defaultPath;
    }
    return trimmed;
  }

  // For absolute URLs, validate they match our origin
  try {
    const url = new URL(trimmed);
    const allowedOrigin =
      baseUrl || process.env.BETTER_AUTH_URL || 'http://localhost:3000';

    // Parse the allowed origin to compare
    const allowed = new URL(allowedOrigin);

    // Check if origins match (protocol + host)
    if (url.origin === allowed.origin) {
      // Return just the pathname + search + hash (relative part)
      return url.pathname + url.search + url.hash;
    }
  } catch {
    // Invalid URL - reject
  }

  // Reject any other format (external URLs, javascript:, data:, etc.)
  return defaultPath;
}
